/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.script.intel

import io.peekandpoke.klang.script.docs.KlangDocsRegistry
import io.peekandpoke.klang.script.docs.receiverChain
import io.peekandpoke.klang.script.docs.typeMatches
import io.peekandpoke.klang.script.runtime.NativeOperatorNames
import io.peekandpoke.klang.script.types.KlangCallable
import io.peekandpoke.klang.script.types.KlangDecl
import io.peekandpoke.klang.script.types.KlangParam
import io.peekandpoke.klang.script.types.KlangProperty
import io.peekandpoke.klang.script.types.KlangSymbol
import io.peekandpoke.klang.script.types.KlangType

private const val MAX_DESCRIPTION_LENGTH = 200

/**
 * A code completion suggestion, independent of any editor framework.
 *
 * @param name       The symbol's name; the typed prefix is matched against it.
 * @param label      What the list shows; the name unless the suggestion inserts more (`q =` for a named argument).
 * @param insertText What accepting the suggestion inserts; the name unless it inserts more (`q = `).
 */
data class CompletionSuggestion(
    val name: String,
    val kind: Kind,
    val detail: String,
    val description: String,
    val isAlias: Boolean,
    val aliasFor: String?,
    val label: String = name,
    val insertText: String = name,
) {
    /** [PARAMETER]: a parameter name offered as a named argument, `name = `. */
    enum class Kind { FUNCTION, PROPERTY, KEYWORD, PARAMETER }
}

/**
 * Provides code completion suggestions based on a [KlangDocsRegistry].
 *
 * This is pure Kotlin logic with no editor dependencies — testable on JVM.
 * The editor adapter (e.g., CodeMirror) converts these suggestions to its own format.
 */
class CompletionProvider(private val registry: KlangDocsRegistry) {

    /**
     * Top-level completions: objects, properties, and top-level functions.
     * Filtered by [prefix] (case-insensitive). Pass empty string for all.
     */
    fun topLevelCompletions(prefix: String): List<CompletionSuggestion> {
        val suggestions = mutableListOf<CompletionSuggestion>()

        for ((name, symbol) in registry.symbols) {
            if (!symbol.hasTopLevelVariant()) {
                continue
            }
            if (prefix.isNotEmpty() && !name.startsWith(prefix, ignoreCase = true)) {
                continue
            }
            suggestions.add(symbol.toTopLevelSuggestion())
        }

        // Add alias completions for top-level symbols
        for ((_, symbol) in registry.symbols) {
            if (!symbol.hasTopLevelVariant()) {
                continue
            }
            for (alias in symbol.aliases) {
                if (prefix.isNotEmpty() && !alias.startsWith(prefix, ignoreCase = true)) {
                    continue
                }
                // A name that is a top-level symbol of its own is offered once, as itself (`density` is
                // listed as an alias of `d`, and is the object `density`)
                if (registry.get(alias)?.hasTopLevelVariant() == true) {
                    continue
                }
                suggestions.add(symbol.toAliasSuggestion(alias))
            }
        }

        return suggestions
    }

    /**
     * Member completions: methods/properties available on the given [receiverType].
     * Filtered by [prefix] (case-insensitive). Pass empty string for all.
     */
    fun memberCompletions(receiverType: KlangType, prefix: String): List<CompletionSuggestion> {
        val matchingSymbols = registry.getVariantsForReceiver(receiverType)
        val suggestions = mutableListOf<CompletionSuggestion>()

        for (symbol in matchingSymbols) {
            if (prefix.isNotEmpty() && !symbol.name.startsWith(prefix, ignoreCase = true)) {
                continue
            }
            // An operator symbol (`__invoke__`) is never typed
            if (NativeOperatorNames.isOperatorName(symbol.name)) {
                continue
            }
            suggestions.add(symbol.toMemberSuggestion(receiverType))
        }

        return suggestions
    }

    /**
     * Completions for the song's own names: one per binding of [locals] (from `AnalyzedAst.localsAt`) whose name
     * starts with [prefix] (case-insensitive; pass an empty string for all).
     *
     * A function-typed local is a [CompletionSuggestion.Kind.FUNCTION], any other a
     * [CompletionSuggestion.Kind.PROPERTY]. The detail says how it was declared and its inferred type when known,
     * in the words of the hover (the kind's [KlangSymbol.LocalKind.display], the type's [KlangType.render]):
     * `let . IgnitorDsl`, `param`.
     */
    fun localCompletions(locals: List<TypeScope.LocalBinding>, prefix: String): List<CompletionSuggestion> {
        return locals
            .filter { prefix.isEmpty() || it.name.startsWith(prefix, ignoreCase = true) }
            .map { it.toLocalSuggestion() }
    }

    /**
     * Named-argument completions: one per parameter of [params] (from `AnalyzedAst.namedArgumentsAt`) whose name
     * starts with [prefix] (case-insensitive; pass an empty string for all), in the order given (the declaration
     * order).
     *
     * The label is `name =`, accepting it inserts `name = `. The detail is the parameter's type as the signature
     * renders it (`vararg ` in front of a vararg's element type), then `default <value>` for a parameter with a
     * documented default, `optional` for another optional one, `required` for a required one (the check reports a
     * required parameter a named call leaves out), nothing for a vararg. The description is the parameter's own;
     * a vararg's says that by name it takes an array.
     */
    fun namedArgumentCompletions(params: List<KlangParam>, prefix: String): List<CompletionSuggestion> {
        return params
            .filter { prefix.isEmpty() || it.name.startsWith(prefix, ignoreCase = true) }
            .map { it.toNamedArgumentSuggestion() }
    }

    /**
     * Import completions: available library names.
     */
    fun importCompletions(availableLibraries: Set<String>): List<CompletionSuggestion> {
        return availableLibraries.map { libName ->
            CompletionSuggestion(
                name = libName,
                kind = CompletionSuggestion.Kind.KEYWORD,
                detail = "library",
                description = "",
                isAlias = false,
                aliasFor = null,
            )
        }
    }
}

// ── Private helpers ────────────────────────────────────────────────────────

private fun KlangSymbol.hasTopLevelVariant(): Boolean {
    return firstTopLevelVariant() != null
}

private fun KlangSymbol.firstTopLevelVariant(): KlangDecl? {
    return variants.firstOrNull { variant ->
        when (variant) {
            is KlangCallable -> variant.receiver == null
            is KlangProperty -> variant.owner == null
        }
    }
}

private fun KlangSymbol.toTopLevelSuggestion(): CompletionSuggestion {
    val displayVariant = firstTopLevelVariant() ?: variants.firstOrNull()

    return CompletionSuggestion(
        name = name,
        kind = displayVariant.toKind(),
        detail = buildDetail(category, displayVariant?.library ?: (getLibrary()?.name ?: "")),
        description = displayVariant?.description?.take(MAX_DESCRIPTION_LENGTH) ?: "",
        isAlias = false,
        aliasFor = null,
    )
}

private fun KlangSymbol.toAliasSuggestion(alias: String): CompletionSuggestion {
    val displayVariant = firstTopLevelVariant() ?: variants.firstOrNull()

    return CompletionSuggestion(
        name = alias,
        kind = displayVariant.toKind(),
        detail = buildDetail(category, displayVariant?.library ?: (getLibrary()?.name ?: ""), "(alias for $name)"),
        description = displayVariant?.description?.take(MAX_DESCRIPTION_LENGTH) ?: "",
        isAlias = true,
        aliasFor = name,
    )
}

private fun KlangSymbol.toMemberSuggestion(receiverType: KlangType): CompletionSuggestion {
    // Most specific first, through the supertypes, like `callableForReceiver`: a method declared on a base type
    // (`SprudelPattern.slow`) shows as a function with its docs on an object that IS one (`perlin.`).
    val matchingVariant = receiverChain(receiverType).firstNotNullOfOrNull { candidate ->
        variants.firstOrNull { variant ->
            val owner = when (variant) {
                is KlangCallable -> variant.receiver
                is KlangProperty -> variant.owner
            }

            typeMatches(owner, candidate)
        }
    }

    return CompletionSuggestion(
        name = name,
        kind = matchingVariant.toKind(),
        detail = buildDetail(receiverType.simpleName, matchingVariant?.library ?: (getLibrary()?.name ?: "")),
        description = matchingVariant?.description?.take(MAX_DESCRIPTION_LENGTH) ?: "",
        isAlias = false,
        aliasFor = null,
    )
}

private fun TypeScope.LocalBinding.toLocalSuggestion(): CompletionSuggestion {
    val known = type

    return CompletionSuggestion(
        name = name,
        kind = if (known?.isFunction == true) CompletionSuggestion.Kind.FUNCTION else CompletionSuggestion.Kind.PROPERTY,
        detail = buildDetail(kind.display.lowercase(), known?.render() ?: ""),
        description = "",
        isAlias = false,
        aliasFor = null,
    )
}

private fun KlangParam.toNamedArgumentSuggestion(): CompletionSuggestion {
    val typeText = if (isVararg) "vararg ${type.render()}" else type.render()
    val defaultText = when {
        isVararg -> ""
        defaultDoc != null -> "default $defaultDoc"
        isOptional -> "optional"
        else -> "required"
    }
    val varargNote = if (isVararg) "By name it takes an array: $name = [...]. " else ""

    return CompletionSuggestion(
        name = name,
        kind = CompletionSuggestion.Kind.PARAMETER,
        detail = buildDetail(typeText, defaultText),
        description = (varargNote + description).trim().take(MAX_DESCRIPTION_LENGTH),
        isAlias = false,
        aliasFor = null,
        label = "$name =",
        insertText = "$name = ",
    )
}

private fun KlangDecl?.toKind(): CompletionSuggestion.Kind {
    return when (this) {
        is KlangCallable -> CompletionSuggestion.Kind.FUNCTION
        is KlangProperty -> CompletionSuggestion.Kind.PROPERTY
        else -> CompletionSuggestion.Kind.PROPERTY
    }
}

private fun buildDetail(vararg parts: String): String {
    return parts.filter { it.isNotEmpty() }.joinToString(" . ")
}
